<template>
  <div class="list-container">
    <div v-if="!settings.onDisabled" class="item-list" :class="{ 'item-list-full-width': settings.fullWidth }" data-testid="on-at-start-container">
      <div class="label-container">
        <div class="label-text">
          <div class="md-title" data-testid="dialogue-list-title">{{ titlePrefix }}{{ settings.onText }}</div>
          <help-tooltip v-if="settings.onSubtitle" type="info" class="subtitle-tooltip" aria-label="Dialogue list tooltip" data-testid="dialogue-list-tooltip">
            {{ settings.onSubtitle }}
          </help-tooltip>
        </div>
        <md-button
          v-if="savedItems.length"
          :disabled="readonly"
          class="md-outlined-button md-primary saved-items-button"
          aria-label="Add Saved Dialogue"
          data-testid="add-saved-dialogue-button"
          @click="state.showAddQuestionDialog = true"
        >
          <md-icon>add</md-icon>Saved Dialogue
        </md-button>
      </div>
      <md-content ref="onList" class="md-scrollbar gray-3 bg scroll-content" @scroll="updateScrollOn">
        <dialogue-list
          :settings="settings"
          :items="state.itemsOn"
          :drag-message="!settings.isParentChild ? state.dragMessageOn : null"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          data-testid="dialogue-list-container"
          @startDrag="startDrag(true)"
          @dropItem="(item) => dropItem(item, false)"
          @editQuestion="editQuestion"
          @editAnswer="editAnswer"
          @editResponse="editResponse"
          @editReason="editReason"
          @playAudio="playAnswerText"
          @editEnabled="(item, value) => editEnabled(item, value, false)"
          @remove="removeOn"
        />
        <md-button
          v-if="showFabOn"
          class="md-fab md-fab-bottom-center md-dense md-primary"
          aria-label="Scroll to Bottom"
          data-testid="scroll-to-bottom-button"
          @click="scrollToBottomOn"
        >
          <md-icon>arrow_downward</md-icon>
        </md-button>
      </md-content>
    </div>
    <div v-if="!settings.offDisabled" class="item-list" :class="{ 'item-list-full-width': settings.fullWidth }" data-testid="off-at-start-container">
      <div class="label-container">
        <div class="label-text">
          <div class="md-title" data-testid="dialogue-list-title">{{ titlePrefix }}{{ settings.offText }}</div>
          <help-tooltip v-if="settings.offSubtitle" type="info" class="subtitle-tooltip">
            {{ settings.offSubtitle }}
          </help-tooltip>
        </div>
      </div>
      <md-content ref="offList" class="md-scrollbar gray-3 bg scroll-content" @scroll="updateScrollOff">
        <dialogue-list
          :settings="settings"
          :items="state.itemsOff"
          :drag-message="!settings.isParentChild ? state.dragMessageOff : null"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          data-testid="dialogue-list-container"
          @startDrag="startDrag(false)"
          @dropItem="(item) => dropItem(item, true)"
          @editQuestion="editQuestion"
          @editAnswer="editAnswer"
          @editResponse="editResponse"
          @editReason="editReason"
          @playAudio="playAnswerText"
          @editEnabled="(item, value) => editEnabled(item, value, true)"
          @remove="removeOff"
        />
        <md-button
          v-if="showFabOff"
          class="md-fab md-fab-bottom-center md-dense md-primary"
          aria-label="Scroll to Bottom"
          data-testid="scroll-to-bottom-button"
          @click="scrollToBottomOff"
        >
          <md-icon>arrow_downward</md-icon>
        </md-button>
      </md-content>
    </div>

    <md-dialog
      :md-active.sync="state.showAddQuestionDialog"
      :md-fullscreen="false"
      data-testid="add-saved-questions-modal-container"
      class="add-questions-dialog gray-3 bg"
    >
      <section-title>Add New: Q&A</section-title>
      <questions-selector
        :questions="unusedItems"
        @updateSelectedQuestions="(questions) => updateSelectedQuestions(questions)"
      />
      <md-dialog-actions>
        <md-button
          class="md-primary"
          aria-label="Cancel"
          data-testid="cancel-button"
          @click="state.showAddQuestionDialog = false"
        >
          Cancel
        </md-button>
        <md-button class="md-raised md-primary" aria-label="Add to List" data-testid="add-to-list-button" @click="addSelectedQuestions">
          Add to List
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { debounce, sortBy, cloneDeep, omit } from 'lodash';
import Chance from 'chance';

import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import DialogueList from './DialogueList';
import SectionTitle from '../common/SectionTitle';
import QuestionsSelector from './QuestionsSelector';

export default {
  name: 'dialogue-editor',
  components: {
    HelpTooltip,
    DialogueList,
    SectionTitle,
    QuestionsSelector,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    savedItems: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: undefined,
    },
    warnings: {
      type: Array,
      default: undefined,
    },
    titlePrefix: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      scrollSettings: {
        onScroll: {
          isVisible: false,
          hasScrolled: false
        },
        offScroll: {
          isVisible: false,
          hasScrolled: false
        }
      },
      state: {
        itemsOn: [],
        itemsOff: [],
        dragMessageOn: null,
        dragMessageOff: null,
        showAddQuestionDialog: false,
        selectedQuestions: [],
      },
    };
  },
  computed: {
    showFabOn() {
      return this.scrollSettings.onScroll.isVisible && !this.scrollSettings.onScroll.hasScrolled;
    },
    showFabOff() {
      return this.scrollSettings.offScroll.isVisible && !this.scrollSettings.offScroll.hasScrolled;
    },
    unusedItems() {
      return this.savedItems.filter((q) => {
        return !(this.items || []).some(
          (question) => q.content.en === question.content.en
        );
      });
    },
  },
  created() {
    this.makeLocalCopy();
    this.emitUpdate = debounce(this.emitUpdate, 2000);
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollbarVis);
    this.updateScrollbarVis();
  },
  beforeDestroy() {
    // $destroy clears this instance's listeners, so a pending update must go now
    this.emitUpdate.flush();
  },
  destroyed() {
    window.removeEventListener('resize', this.updateScrollbarVis);
  },
  methods: {
    addQuestionList(items) {
      for (const item of items) {
        const insertIndex = this.state.itemsOn.length - 1;
        this.state.itemsOn.splice(insertIndex, 0, this.createItem(item.content.en, true, insertIndex));
      }
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    makeLocalCopy() {
      if (this.settings.onDisabled) {
        this.state.itemsOn = [];
      } else {
        this.state.itemsOn = sortBy(
          cloneDeep(this.items).filter(item => item.options.enabled),
          'options.position'
        );
      }

      this.state.itemsOff = sortBy(
        cloneDeep(this.items).filter(item => !item.options.enabled),
        'options.position'
      );

      this.updateDummyItems();
      this.updatePositions();
    },
    findParent(item, isOff) {
      let parentList;
      if (isOff) {
        parentList = this.state.itemsOff;
      } else {
        parentList = this.state.itemsOn;
      }
      return parentList.find(parent => parent.children && parent.children.find(child => child.id === item.id));
    },
    editQuestion(item, value) {
      item.content.en = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    editAnswer(item, value) {
      item.options.answer.en = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    editResponse(item, value) {
      item.options.response = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    editReason(item, value) {
      item.options.reason.en = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    convertNewItem(item) {
      if (item.isNew) {
        this.$analytics.event('Authoring Tool', 'question_add_question'); // tbd
        item.isNew = false;
      }
    },
    updatePositions() {
      let parentIndex = 0;
      if (!this.settings.onDisabled) {
        for (const parent of this.state.itemsOn) {
          delete parent.parent;
          parent.options.position = parentIndex++;
          let childIndex = 0;
          for (const child of parent.children || []) {
            child.parent = parent;
            child.options.position = childIndex++;
          }
        }
      }
      for (const parent of this.state.itemsOff) {
        delete parent.parent;
        parent.options.position = parentIndex++;
        let childIndex = 0;
        for (const child of parent.children || []) {
          child.parent = parent;
          child.options.position = childIndex++;
        }
      }
    },
    updateDummyItems() {
      // Because of drag and drop, the dummy item may not be at the end.
      // Therefore delete the existing dummy items and add new ones.
      this.state.itemsOn = this.state.itemsOn.filter(item => !item.isNew);
      this.appendNewItem(false);
      if (this.settings.isParentChild) {
        for (const item of this.state.itemsOn) {
          if (item.children) {
            item.children = item.children.filter(childItem => !childItem.isNew);
          }
          this.appendNewChild(item, false);
        }
      }
      this.state.itemsOff = this.state.itemsOff.filter(item => !item.isNew);
      this.appendNewItem(true);
      if (this.settings.isParentChild) {
        for (const item of this.state.itemsOff) {
          if (item.children) {
            item.children = item.children.filter(childItem => !childItem.isNew);
          }
          this.appendNewChild(item, true);
        }
      }
    },
    editEnabled(item, value) {
      item.options.enabled = value;
      this.emitUpdate();
    },
    appendNewItem(isOff) {
      const newItem = this.createItem('', !isOff, undefined, true);
      if (isOff) {
        this.state.itemsOff.push(newItem);
      } else {
        this.state.itemsOn.push(newItem);
      }
      return newItem;
    },
    appendNewChild(parentItem, isOff) {
      if (!parentItem.children) {
        Vue.set(parentItem, 'children', []);
      }
      const childItem = this.createItem('', !isOff, parentItem.children.length, true);
      childItem.parent = parentItem;
      parentItem.children.push(childItem);
    },
    playAnswerText(item) {
      this.$emit('playAudio', item)
    },
    removeOn(item) {
      const parentItem = this.findParent(item, false);
      if (parentItem) {
        const childIndex = parentItem.children.findIndex(child => child.id === item.id);
        if (childIndex >= 0) {
          parentItem.children.splice(childIndex, 1);
          this.emitUpdate();
        }
      } else {
        const index = this.state.itemsOn.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.state.itemsOn.splice(index, 1);
          this.emitUpdate();
        }
      }
      this.updateDummyItems();
      this.updatePositions();
    },
    removeOff(item) {
      const parentItem = this.findParent(item, true);
      if (parentItem) {
        const childIndex = parentItem.children.findIndex(child => child.id === item.id);
        if (childIndex >= 0) {
          parentItem.children.splice(childIndex, 1);
          this.emitUpdate();
        }
      } else {
        const index = this.state.itemsOff.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.state.itemsOff.splice(index, 1);
          this.emitUpdate();
        }
      }
      this.updateDummyItems();
      this.updatePositions();
    },
    startDrag(isOnList) {
      if (isOnList) {
        this.state.dragMessageOn = 'Drag and drop here to reorder';
        this.state.dragMessageOff = 'Drag and drop here to turn off';
      } else {
        this.state.dragMessageOn = 'Drag and drop here to turn on';
        this.state.dragMessageOff = 'Drag and drop here to reorder';
      }
    },
    // eslint-disable-next-line no-unused-vars
    dropItem(item, isOff) {
      this.updateDummyItems();
      this.updatePositions();
      this.state.dragMessageOn = null;
      this.state.dragMessageOff = null;
      // Item has already been moved, update enabled status
      if (!this.settings.onDisabled) {
        for (const item of this.state.itemsOn) {
          item.options.enabled = true;
        }
        for (const item of this.state.itemsOff) {
          item.options.enabled = false;
        }
      }
      this.emitUpdate();
    },
    createItem(content, enabled, position, isNew = undefined) {
      const chance = new Chance();
      return {
        id: chance.guid(),
        isNew: isNew,
        content: this.settings.isQuestionDisabled ? undefined : { en: content },
        options: {
          enabled: this.settings.onDisabled ? undefined : enabled,
          answer: this.settings.isAnswerDisabled ? undefined :  { en: '' },
          response: this.settings.isAI && !this.settings.isQuestionDisabled ? 'AI' : undefined,
          reason: this.settings.isAI ? { en: '' } : undefined,
          position,
        },
      };
    },
    emitUpdate() {
      const updatedItems = this.state.itemsOn
        .concat(this.state.itemsOff)
        .filter(item => !item.isNew)
        .map(item => {
          return {
            ...omit(item, ['parent', 'isNew']),
            children: item.children
              ? item.children.filter(child => !child.isNew).map(child => omit(child, ['parent', 'isNew']))
              : undefined
          };
        });
      this.$emit('update', updatedItems);
    },
    updateScrollbarVis() {
      this.scrollSettings.onScroll.isVisible = !!(this.$refs.onList && this.$refs.onList.$el.scrollHeight > this.$refs.onList.$el.clientHeight);
      this.scrollSettings.offScroll.isVisible = !!(this.$refs.offList && this.$refs.offList.$el.scrollHeight > this.$refs.offList.$el.clientHeight);
    },
    updateScrollOn() {
      this.scrollSettings.onScroll.hasScrolled = true;
    },
    scrollToBottomOn() {
      if (this.$refs.onList) {
        this.$refs.onList.$el.scrollTo({
          top: this.$refs.onList.$el.scrollHeight,
          left: 0,
          behavior: 'smooth'
        })
        this.scrollSettings.onScroll.hasScrolled = true;
      }
    },
    updateScrollOff() {
      this.scrollSettings.offScroll.hasScrolled = true;
    },
    scrollToBottomOff() {
      if (this.$refs.offList) {
        this.$refs.offList.$el.scrollTo({
          top: this.$refs.offList.$el.scrollHeight,
          left: 0,
          behavior: 'smooth'
        })
        this.scrollSettings.offScroll.hasScrolled = true;
      }
    },
    updateSelectedQuestions(questions) {
      console.log(questions)
      this.state.selectedQuestions = questions;
    },
    addSelectedQuestions() {
      this.addQuestionList(this.state.selectedQuestions)
      this.$analytics.event('Authoring Tool', 'questions_add_new_from_list', null, this.state.selectedQuestions.length);
      this.state.selectedQuestions = [];
      this.state.showAddQuestionDialog = false;
    }
  }
}
</script>

<style scoped>
.md-field:first-child {
  margin-right: 20px;
}

.list-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: calc(100% - 40px);
}

.label-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 36px;
}

.label-text {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.subtitle-tooltip {
  margin-left: 6px;
}

.saved-items-button {
  margin: 0;
  margin-right: 6px;
}

.item-list {
  width: 100%;
  max-width: 50%;
  display: table;
  height: 100%;
}

.item-list-full-width {
  max-width: 100%;
}

.item-list:not(:last-child) {
  margin-right: 15px;
  padding-right: 15px;
  border-right: 1px solid #9f9f9f;
}

.scroll-content {
  position: relative;
  overflow-y: auto;
  height: 100%;
}

.section-title {
  margin-bottom: 10px;
}

.add-questions-dialog {
  padding: 20px 35px;
  overflow: auto;
}

@media (max-width: 599px) {
  .add-questions-dialog {
    width: 400px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .add-questions-dialog {
    width: 600px;
  }
}

@media (min-width: 960px) {
  .add-questions-dialog {
    width: 800px;
  }
}
</style>